/* SpawnAnything — formations and army orders for the battle sim (pure, worker-safe, deterministic).
   Formation (setup): starting layout in count mode; the turtle also walks slower and takes less ranged damage.
   Orders (commands via cmdQ, { t: 'formationOrder', team, o, x?, z?, s? }):
     0 advance (default behaviour), 1 hold (fight only around the anchor), 2 rally (march to a point, fight on the way),
     3 flank (swing around the enemy's side, s: 1 right, -1 left, 0 both wings).
   Only +, -, *, /, sqrt, floor, min, max, abs and the seeded R() are used (lockstep). */
import { computeFlowField, sampleFlow } from '../flowfield.js';
import { cellAt, nearestWalkable } from '../../world/terrain.js';

export const FORMATION_KEYS = ['block', 'linie', 'keil', 'schildkroete', 'zange'];
export const FM_BLOCK = 0, FM_LINE = 1, FM_WEDGE = 2, FM_TURTLE = 3, FM_PINCER = 4;
export const ORDER_KEYS = ['vorruecken', 'halten', 'sammeln', 'flanke'];
export const ORD_ADVANCE = 0, ORD_HOLD = 1, ORD_RALLY = 2, ORD_FLANK = 3;
export const TURTLE_SPEED = 0.7, TURTLE_RANGED = 0.5;
const FF_EVERY = 30; // flank flow fields: recomputed every second per team (staggered)
const FAR = 20;     // farther than this from its goal a unit follows the order's flow field (terrain)

export function createFormationState() {
  return {
    kind: new Uint8Array(2), ord: new Uint8Array(2), side: new Int8Array(2),
    rx: new Float64Array(2), rz: new Float64Array(2), // rally point per team
    rk: Float64Array.of(1, 1),                          // ranged damage factor a team takes
    AX: null, AZ: null,                                 // hold anchors per unit (NaN = not set yet)
    cx: new Float64Array(2), cz: new Float64Array(2), n: new Int32Array(2), // alive centroid per team
    ax: new Float64Array(2), az: new Float64Array(2),   // unit axis own centroid -> enemy centroid
    dep: new Float64Array(2),                           // enemy depth toward team t (flank)
    wp: new Float64Array(8),                            // flank shift per wing: [t*4 + (right ? 0 : 2)] = dx, dz
    rff: [null, null], fff: [null, null, null, null], fDirty: new Uint8Array(2),
    lim: 1e9, mv: new Float64Array(2), ext: new Float64Array(2), src: new Int32Array(1),
  };
}

/** setup: formation per team from msg.formation ([k0, k1], invalid = block); resets all orders.
    Replaces the stats of a turtle team's unit groups (army = the worker's group list, each with `team`) by slower copies
    (the message objects stay untouched). */
export function formationSetup(S, msg, army) {
  const f = Array.isArray(msg.formation) ? msg.formation : null;
  for (let t = 0; t < 2; t++) {
    const k = f ? f[t] : 0;
    S.kind[t] = Number.isInteger(k) && k >= 0 && k < FORMATION_KEYS.length ? k : 0;
    S.ord[t] = ORD_ADVANCE; S.side[t] = 0; S.rx[t] = S.rz[t] = 0; S.fDirty[t] = 0;
    S.rk[t] = 1; S.rff[t] = null; S.fff[t * 2] = S.fff[t * 2 + 1] = null;
    if (S.kind[t] === FM_TURTLE) { for (let k = 0; k < army.length; k++) if (army[k] && army[k].team === t) army[k] = { ...army[k], speed: army[k].speed * TURTLE_SPEED }; S.rk[t] = TURTLE_RANGED; }
  }
  S.AX = S.AZ = null; S.lim = 1e9;
  for (const a of [S.cx, S.cz, S.n, S.ax, S.az, S.dep, S.wp]) a.fill(0); // derived per tick, but part of snapshots
}

/** Starting layout (count mode) for formation `kind` != block: writes X/Z/ROT of army A's units,
    extents to S.ext ([x, z]). lim = largest |z| the field allows (terrain), R = the sim's PRNG. */
export function formationLayout(S, kind, A, t, gap, R, X, Z, ROT, lim) {
  const n = A.count, dir = t === 0 ? -1 : 1, yaw = t === 0 ? Math.PI / 2 : -Math.PI / 2;
  const tight = kind === FM_TURTLE;
  const sp = tight ? A.r * 2 * 1.04 + 0.05 : A.r * 2 * 1.22 + 0.15;
  const jit = tight ? 0 : sp * 0.12;
  const front = dir * (gap / 2 + A.r);
  const maxCols = Math.max(1, Math.floor((2 * lim) / sp));
  let ex = 0, ez = 0;
  const put = (k, along, lat) => {
    const i = A.start + k;
    Z[i] = lat + (R() - 0.5) * jit;
    X[i] = front + dir * along + (R() - 0.5) * jit;
    ROT[i] = yaw;
    const ax = Math.abs(X[i]) + A.r, az = Math.abs(Z[i]) + A.r;
    if (ax > ex) ex = ax; if (az > ez) ez = az;
  };
  // rows of `cols` units, centred on z = zc, starting `k0`; returns the next unit index
  const block = (k0, cnt, cols, zc, spX, horn) => {
    const rows = Math.ceil(cnt / cols);
    for (let k = 0; k < cnt; k++) {
      const col = k % cols, row = (k / cols) | 0;
      const inRow = row === rows - 1 ? cnt - row * cols : cols;
      const c = col - (inRow - 1) / 2;
      // pincer: the outer columns of a wing reach forward (horn > 0: outer edge on +z side, < 0: on -z side)
      const fwd = horn ? Math.min(gap * 0.3, Math.max(0, (horn > 0 ? c : -c) + (cols - 1) / 2) * sp * 0.35) : 0;
      put(k0 + k, row * spX - fwd, zc + c * sp);
    }
    return k0 + cnt;
  };
  if (kind === FM_LINE) {
    const cols = Math.min(maxCols, Math.max(1, Math.ceil(Math.sqrt(n * 9))));
    block(0, n, cols, 0, sp, 0);
  } else if (kind === FM_TURTLE) {
    const cols = Math.min(maxCols, Math.max(1, Math.ceil(Math.sqrt(n))));
    block(0, n, cols, 0, sp, 0);
  } else if (kind === FM_WEDGE) {
    // tip first: row r holds 2r + 1 units (capped by the field width), last row centred
    let k = 0, r = 0;
    while (k < n) {
      const inRow = Math.min(n - k, 2 * r + 1, maxCols);
      for (let c = 0; c < inRow; c++) put(k + c, r * sp * 0.9, (c - (inRow - 1) / 2) * sp);
      k += inRow; r++;
    }
  } else if (kind === FM_PINCER) {
    const h0 = Math.ceil(n / 2), h1 = n - h0;
    const cols = Math.max(1, Math.min(Math.floor(maxCols / 3), Math.ceil(Math.sqrt(h0 * 1.6))));
    const wingW = cols * sp, hole = Math.max(8, wingW * 0.45);
    const zc = Math.min(hole / 2 + wingW / 2, Math.max(0, lim - wingW / 2));
    const k = block(0, h0, cols, -zc, sp, -1);
    if (h1) block(k, h1, cols, zc, sp, 1);
  } else {
    const cols = Math.min(maxCols, Math.max(1, Math.ceil(Math.sqrt(n * 2.2))));
    block(0, n, cols, 0, sp, 0);
  }
  S.ext[0] = ex; S.ext[1] = ez;
}

/** Apply one validated order. Returns true when accepted. lim = half size of the walkable field. */
export function formationCmd(S, c, X, Z, ALIVE, army, TR, TP, lim) {
  if (!c || (c.team !== 0 && c.team !== 1)) return false;
  const t = c.team, o = c.o, A = army[t];
  if (!Number.isInteger(o) || o < 0 || o >= ORDER_KEYS.length || !A) return false;
  if (o === ORD_RALLY) {
    if (!Number.isFinite(c.x) || !Number.isFinite(c.z)) return false;
    let x = Math.max(-lim, Math.min(lim, c.x)), z = Math.max(-lim, Math.min(lim, c.z));
    if (TR) {
      // rally in water: use the nearest walkable cell, then path there with a flow field
      const j = nearestWalkable(TR, x, z, 60); if (j < 0) return false;
      if (j !== cellAt(TR, x, z)) { x = TR.origin + ((j % TR.n) + 0.5) * TR.cell; z = TR.origin + (((j / TR.n) | 0) + 0.5) * TR.cell; }
      S.src[0] = j; S.rff[t] = computeFlowField(TP, S.src, S.rff[t] || undefined);
    }
    S.rx[t] = x; S.rz[t] = z;
  } else if (o === ORD_FLANK) {
    S.side[t] = c.s === -1 || c.s === 1 ? c.s : 0; S.fDirty[t] = 1;
  } else if (o === ORD_HOLD) {
    const N = X.length;
    if (!S.AX || S.AX.length !== N) { S.AX = new Float32Array(N).fill(NaN); S.AZ = new Float32Array(N).fill(NaN); }
    for (let i = A.start; i < A.end; i++) { if (ALIVE[i]) { S.AX[i] = X[i]; S.AZ[i] = Z[i]; } else { S.AX[i] = NaN; S.AZ[i] = NaN; } }
  }
  S.lim = lim; S.ord[t] = o;
  return true;
}

/** Once per tick, before the unit loop: forget anchors of the dead, centroids, flank moves (+ flow fields on terrain). */
export function formationTick(S, X, Z, ALIVE, army, TR, TP, stepN) {
  const o0 = S.ord[0], o1 = S.ord[1];
  if (!o0 && !o1) return;
  for (let t = 0; t < 2; t++) {
    if (S.ord[t] !== ORD_HOLD || !S.AX) continue;
    const A = army[t], AX = S.AX;
    for (let i = A.start; i < A.end; i++) if (!ALIVE[i] && AX[i] === AX[i]) { AX[i] = NaN; S.AZ[i] = NaN; } // the slot may be reused by a spawn
  }
  if (o0 < ORD_RALLY && o1 < ORD_RALLY) return;
  for (let t = 0; t < 2; t++) {
    const A = army[t]; let sx = 0, sz = 0, n = 0;
    for (let i = A.start; i < A.end; i++) if (ALIVE[i]) { sx += X[i]; sz += Z[i]; n++; }
    S.n[t] = n; S.cx[t] = n ? sx / n : 0; S.cz[t] = n ? sz / n : 0;
  }
  for (let t = 0; t < 2; t++) if (S.ord[t] === ORD_FLANK) flankPlan(S, t, X, Z, ALIVE, army, TR, TP, stepN);
}

// flank: attack axis, enemy extent, and per wing the shift (waypoint - group centre) that S.wp holds
function flankPlan(S, t, X, Z, ALIVE, army, TR, TP, stepN) {
  const e = 1 - t, A = army[t], E = army[e];
  let ax = S.cx[e] - S.cx[t], az = S.cz[e] - S.cz[t];
  const l = Math.sqrt(ax * ax + az * az);
  if (l > 1e-6) { ax /= l; az /= l; } else { ax = t === 0 ? 1 : -1; az = 0; }
  S.ax[t] = ax; S.az[t] = az;
  // enemy half width across (lat) and depth toward us (dep) the attack axis; right = +(-az, ax)
  let lat = 0, dep = 0;
  for (let i = E.start; i < E.end; i++) {
    if (!ALIVE[i]) continue;
    const dx = X[i] - S.cx[e], dz = Z[i] - S.cz[e];
    const a = -(dx * ax + dz * az), s = dx * -az + dz * ax, sa = s < 0 ? -s : s;
    if (sa > lat) lat = sa;
    if (a > dep) dep = a;
  }
  S.dep[t] = dep;
  // own half width and the centre of each wing ("both sides" splits the army at its centre line)
  let own = 0, rx = 0, rz = 0, rn = 0, lx = 0, lz = 0, ln = 0;
  for (let i = A.start; i < A.end; i++) {
    if (!ALIVE[i]) continue;
    const s = (X[i] - S.cx[t]) * -az + (Z[i] - S.cz[t]) * ax, sa = s < 0 ? -s : s;
    if (sa > own) own = sa;
    if (s > 0 || (s === 0 && (i & 1))) { rx += X[i]; rz += Z[i]; rn++; } else { lx += X[i]; lz += Z[i]; ln++; }
  }
  const side = S.side[t], off = lat + 8 + A.r * 2 + (side ? own : own * 0.5);
  const ff = TR && (S.fDirty[t] || stepN % FF_EVERY === t * (FF_EVERY / 2));
  for (let k = 0; k < 2; k++) {
    const s = k === 0 ? 1 : -1, o = t * 4 + k * 2;
    let wx = S.cx[e] - az * s * off - ax * dep * 0.5, wz = S.cz[e] + ax * s * off - az * dep * 0.5;
    if (wx < -S.lim) wx = -S.lim; else if (wx > S.lim) wx = S.lim;
    if (wz < -S.lim) wz = -S.lim; else if (wz > S.lim) wz = S.lim;
    // the whole army (one side) or each wing (both sides) shifts by waypoint - its centre, keeping its shape
    const gn = k === 0 ? rn : ln;
    const gx = side || !gn ? S.cx[t] : (k === 0 ? rx : lx) / gn, gz = side || !gn ? S.cz[t] : (k === 0 ? rz : lz) / gn;
    S.wp[o] = wx - gx; S.wp[o + 1] = wz - gz;
    if (ff && (!side || side === s)) {
      const j = nearestWalkable(TR, wx, wz, 60);
      if (j < 0) S.fff[t * 2 + k] = null;
      else { S.src[0] = j; S.fff[t * 2 + k] = computeFlowField(TP, S.src, S.fff[t * 2 + k] || undefined); }
    }
  }
  S.fDirty[t] = 0;
}

// walk towards (gx, gz); on terrain along flow field f when it has a direction here
function walkTo(S, x, z, gx, gz, spd, f, TR) {
  if (TR && f && sampleFlow(f, TR, x, z, S.mv)) { S.mv[0] *= spd; S.mv[1] *= spd; return 1; }
  const dx = gx - x, dz = gz - z, l = Math.sqrt(dx * dx + dz * dz) || 1e-4;
  S.mv[0] = dx / l * spd; S.mv[1] = dz / l * spd;
  return 1;
}

/** Steering of AI unit i of team t under the team's order, given its target point (tx, tz) at distance d.
    Returns 0 = keep the normal behaviour, 1 = walk along S.mv, 2 = stand still (keeps facing the target). */
export function formationSteer(S, i, t, A, x, z, tx, tz, d, reach, spd, TR) {
  const o = S.ord[t];
  const leash = A.melee ? A.r * 2 + 3 : 0; // melee may step out this far to meet an enemy
  if (o === ORD_HOLD) {
    if (!S.AX) return 0;
    let hx = S.AX[i], hz = S.AZ[i];
    if (hx !== hx) { S.AX[i] = hx = x; S.AZ[i] = hz = z; } // spawned after the order: hold where it appeared
    const ex = tx - hx, ez = tz - hz, rr = reach + leash;
    if (ex * ex + ez * ez <= rr * rr) return 0;
    const dx = hx - x, dz = hz - z, back = A.r + 0.6;
    if (dx * dx + dz * dz <= back * back) { S.mv[0] = 0; S.mv[1] = 0; return 2; }
    return walkTo(S, x, z, hx, hz, spd, null, TR);
  }
  if (o === ORD_RALLY) {
    if (d <= reach + leash) return 0; // fight on the way
    // the army moves as a group until its centre is on the point; stragglers close up to the group's disc
    const rad = A.r * 2 + Math.sqrt(S.n[t] > 0 ? S.n[t] : 1) * A.r * 1.4;
    let ox = x - S.cx[t], oz = z - S.cz[t];
    const ol = Math.sqrt(ox * ox + oz * oz);
    if (ol > rad) { ox *= rad / ol; oz *= rad / ol; }
    const gx = S.rx[t] + ox, gz = S.rz[t] + oz, dx = gx - x, dz = gz - z, dl = dx * dx + dz * dz, stop = A.r + 0.6;
    if (dl <= stop * stop) { S.mv[0] = 0; S.mv[1] = 0; return 2; }
    return walkTo(S, x, z, gx, gz, spd, dl > FAR * FAR ? S.rff[t] : null, TR);
  }
  if (o === ORD_FLANK) {
    if (d <= reach + leash + 2) return 0;
    const ax = S.ax[t], az = S.az[t], e = 1 - t;
    // level with the enemy's front: turn in and attack its side (normal behaviour)
    if ((x - S.cx[e]) * ax + (z - S.cz[e]) * az >= -S.dep[t] * 0.3) return 0;
    let s = S.side[t];
    if (!s) { const lat = (x - S.cx[t]) * -az + (z - S.cz[t]) * ax; s = lat > 0 || (lat === 0 && (i & 1)) ? 1 : -1; }
    const k = s > 0 ? 0 : 1, dx = S.wp[t * 4 + k * 2], dz = S.wp[t * 4 + k * 2 + 1], dl = dx * dx + dz * dz;
    if (dl < 16) return 0;
    return walkTo(S, x, z, x + dx, z + dz, spd, dl > FAR * FAR ? S.fff[t * 2 + k] : null, TR);
  }
  return 0;
}
